import { Box, Theme } from "@outschool/backpack";
import { UserMode } from "@outschool/routes";
import { CurrentUser, useSession } from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { DEFAULT_CTAS, useBanners } from "@outschool/ui-components-website";
import {
  Loading,
  TrackingPage,
  TrackingPageSection
} from "@outschool/ui-legacy-component-library";
import { Redirect, simpleRandomId, useIsMobile } from "@outschool/ui-utils";
import React, { useState } from "react";

import {
  getOrgAdminRoutesFromUser,
  isBuyerOrgAdmin
} from "../../../lib/BuyerOrgAdmins";
import { useLastUserMode, userModeRootPath } from "../../../lib/UserMode";
import SimpleLayout from "../../layouts/SimpleLayout";
import { HEADER_WIDTH } from "../../nav/Header";
import DashboardNav from "../components/DashboardNav";
import LearnerBanner from "../components/LearnerBanner";
import { useParentDashboardRedirects } from "../hooks/useParentDashboardRedirects";

function RedirectToOrgAdmin({ user }: { user: CurrentUser }) {
  const { homeRoute } = getOrgAdminRoutesFromUser(user);

  return <Redirect to={homeRoute} />;
}

export default function ParentDashboardLayout({
  children
}: {
  children: React.ReactNode;
}) {
  const { stripeIsLoading } = useParentDashboardRedirects();
  const { currentUser, currentUserIsLoading } = useSession();
  const [pageImpressionId] = useState(simpleRandomId());
  const [lastUserMode] = useLastUserMode();
  const buyerOrgAdminModeEnabled = useFeatureFlag(
    "partners-buyer-org-admin-mode"
  );
  const isMobile = useIsMobile();
  const [learnerBannerHeight, setLearnerBannerHeight] = useState(0);
  const selectedParentCta = useBanners("LoggedInHome Top", DEFAULT_CTAS);
  const additionalTrackingProps = !!selectedParentCta?.name
    ? { shownCta: selectedParentCta.name }
    : {};

  const loading =
    currentUser === undefined || currentUserIsLoading || stripeIsLoading;

  const learners = currentUser?.children ?? [];
  const isParent = learners.length > 0;

  if (lastUserMode === UserMode.Educator) {
    return <Redirect to={userModeRootPath(lastUserMode)} />;
  } else if (buyerOrgAdminModeEnabled && isBuyerOrgAdmin(currentUser)) {
    return <RedirectToOrgAdmin user={currentUser} />;
  }

  return (
    <Box sx={{ backgroundColor: "grey.50" }}>
      <TrackingPage
        parent={currentUser || undefined}
        name="logged_in_home"
        uniqueId="logged_in_home"
        pageImpressionIdDefined={pageImpressionId}
      >
        {setImpressionNode => (
          <SimpleLayout
            ref={node => {
              if (node) {
                setImpressionNode(node);
              }
            }}
            allowCategoriesNavbar
          >
            {loading ? (
              <Loading />
            ) : (
              <>
                <TrackingPageSection
                  name="parent dashboard"
                  uniqueId="parent dashboard"
                  uniqueProperties={{
                    isMobile,
                    isParent,
                    ...additionalTrackingProps
                  }}
                >
                  {setImpressionNode => (
                    <div
                      ref={node => {
                        if (node) {
                          setImpressionNode(node);
                        }
                      }}
                    />
                  )}
                </TrackingPageSection>
                <LearnerBanner
                  setLearnerBannerHeight={setLearnerBannerHeight}
                />
                <Box
                  sx={(theme: Theme) => ({
                    py: 24,
                    display: "grid",
                    gridTemplateColumns: "236px 1fr",
                    gap: 30,
                    maxWidth: HEADER_WIDTH,
                    margin: "0 auto",
                    "@media (max-width: 1440px)": {
                      px: 24
                    },
                    [theme.breakpoints.down("md")]: {
                      display: "block",
                      padding: 0
                    }
                  })}
                >
                  <DashboardNav learnerBannerHeight={learnerBannerHeight} />
                  <Box>{children}</Box>
                </Box>
              </>
            )}
          </SimpleLayout>
        )}
      </TrackingPage>
    </Box>
  );
}
